
import { computed, defineComponent, HTMLAttributes, onMounted, ref } from 'vue'

import { ModelEvent, NativeEvent } from '@/app/types/app/event'

import getUniqId from '@/utils/getUniqId'

import IconButton from '@C/ui/button/IconButton.vue'
import CloseIcon from '@C/ui/icons/CloseIcon.vue'

export default defineComponent({
  name: 'AppInput',
  components: { IconButton, CloseIcon },
  inheritAttrs: false,
  props: {
    clearable: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    isError: {
      type: Boolean,
      default: false,
    },
    errorText: {
      type: String,
      default: '',
    },
    modelModifiers: {
      type: Object,
      default: () => ({}),
    },
    forceAutofocus: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
  },
  emits: [ModelEvent.UpdateModelValue, NativeEvent.Blur],
  setup(props, { slots, attrs, emit }) {
    const inputRef = ref<HTMLInputElement>()

    const inputId = computed(
      () => separatedAttrs.value.inputAttrs.id ?? getUniqId(),
    )

    const separatedAttrs = computed(() => {
      const { class: wrapperClasses, ...inputAttrs } = attrs

      return { wrapperClasses, inputAttrs }
    })

    const inputClasses = computed(() => ({
      'app-input__field_invalid': props.isError,
      'app-input__field_prepend': slots.prepend !== undefined,
      'app-input__field_append': slots.append !== undefined,
    }))

    const inputValue = computed({
      get() {
        return separatedAttrs.value.inputAttrs.modelValue as string
      },
      set(value: string) {
        let modifiedValue: string = value

        if (props.modelModifiers.lowercase) {
          modifiedValue = value.toLowerCase()
        }

        emit(ModelEvent.UpdateModelValue, modifiedValue)
      },
    })

    const hasValue = computed(() => !!inputValue.value?.length)

    function clearValue() {
      inputValue.value = ''
    }

    onMounted(() => {
      if (props.forceAutofocus) {
        setTimeout(() => inputRef.value?.focus(), 100)
      }
    })

    return {
      inputRef,
      inputId,
      separatedAttrs,
      inputClasses,
      inputValue,
      hasValue,
      clearValue,
    }
  },
})
